import {
    Grid2,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TablePagination,
    TableRow,
    Tooltip,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import { Link } from 'react-router-dom';
import SkeletonTable from '../../../components/atom/SkeletonTable';
import { MODAL_TYPE, useModals } from '../../../hooks/useModal';
import useNotify from '../../../hooks/useNotify';
import SortableTableCell from '../../../components/atom/SortableTableCell';
import { TABLE_ROW_COUNT_OPTIONS } from '../../../helpers/constants';
import { handleChangeRowsPerPage, handlePageChange } from '../../../helpers/handleTableFunctions';
import { useTranslation } from 'react-i18next';
import { deleteUserFromVenue } from '@openapi/users/users';
import { getRoleLabelKey } from '../../../helpers/capabilities';

const UsersTable = ({
    rows,
    limit,
    page,
    setParams,
    total,
    loading,
    reloadData,
    sort,
    sortDir,
    setSorting,
    venueId,
}) => {
    const { t } = useTranslation();
    const { showModal } = useModals();
    const { notifyError, notifySuccess } = useNotify();
    const [deletingId, setDeletingId] = useState(null);

    const deleteUser = useCallback(
        async (id) => {
            setDeletingId(id);
            try {
                await deleteUserFromVenue(id, venueId);
                notifySuccess(t('users.notifications.deleted'));
                reloadData();
            } catch (_error) {
                notifyError(t('users.notifications.deleteError'));
            } finally {
                setDeletingId(null);
            }
        },
        [venueId, notifySuccess, notifyError, reloadData, t],
    );

    useEffect(() => {
        if (rows?.length === 0 && page > 0) {
            setParams({ limit, page: page - 1 });
        }
    }, [rows, limit, page, setParams]);

    return (
        <Grid2 size={{ xs: 12 }}>
            <TableContainer>
                <Table sx={{ minWidth: 560 }}>
                    <TableHead>
                        <TableRow>
                            <SortableTableCell
                                arrow={sort === 'name' ? sortDir : ''}
                                onClick={() => setSorting('name')}
                            >
                                {t('users.columns.name')}
                            </SortableTableCell>
                            <SortableTableCell
                                arrow={sort === 'email' ? sortDir : ''}
                                onClick={() => setSorting('email')}
                            >
                                {t('users.columns.email')}
                            </SortableTableCell>
                            <TableCell>{t('users.columns.role')}</TableCell>
                            <TableCell />
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <SkeletonTable rows={limit} cells={4} />
                        ) : (
                            rows?.map((row) => {
                                const roleLabelKey = getRoleLabelKey(
                                    row.capabilities,
                                    row.isSuperAdmin,
                                );
                                const roleLabel =
                                    roleLabelKey === 'custom'
                                        ? t('capabilities.custom')
                                        : t(`users.roleLabels.${roleLabelKey}`);
                                return (
                                    <TableRow key={row.id}>
                                        <TableCell>{row.name}</TableCell>
                                        <TableCell>{row.email}</TableCell>
                                        <TableCell>{roleLabel}</TableCell>
                                        <TableCell align="right">
                                            {row.canManage && (
                                                <>
                                                    <Link
                                                        to={`/venues/${venueId}/users/edit/${row.id}`}
                                                    >
                                                        <Tooltip title={t('common.edit')}>
                                                            <IconButton>
                                                                <EditIcon />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Link>
                                                    <IconButton
                                                        onClick={() => {
                                                            showModal(MODAL_TYPE.CONFIRMATION, {
                                                                title: t('confirmationModal.title'),
                                                                content: t(
                                                                    'confirmationModal.content',
                                                                ),
                                                                confirm: () => deleteUser(row.id),
                                                            });
                                                        }}
                                                        disabled={deletingId === row.id}
                                                    >
                                                        <Tooltip title={t('common.delete')}>
                                                            <DeleteIcon />
                                                        </Tooltip>
                                                    </IconButton>
                                                </>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                );
                            })
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                count={total || 0}
                rowsPerPage={limit}
                component="div"
                page={rows?.length <= 0 ? 0 : page}
                onPageChange={(_, newPage) => handlePageChange(newPage, setParams)}
                rowsPerPageOptions={TABLE_ROW_COUNT_OPTIONS}
                onRowsPerPageChange={(event) => handleChangeRowsPerPage(event, setParams)}
                labelRowsPerPage={t('labelRowsPerPage')}
                labelDisplayedRows={({ from, to, count }) => `${from}-${to} / ${count}`}
            />
        </Grid2>
    );
};

export default UsersTable;
